// Configuration - Define VIP variants and free product variants
const CONFIG = {
  vipVariants: ['46436299505856'],
  // ✅ NOW SUPPORTS MULTIPLE FREE VARIANTS
  freeProductVariants: ['46579815874752', '47256335974592'],
  sellingPlans: {
    '46436299505856': 2710044864,
  },
  discountCodes: {
    vipMember: 'VIPMEMBEROFF',
    nonVipMember: 'ADD_VIP_PRODUCT_TO_THE_CART_FOR_DISCOUNT',
    testVip: 'TESTVIPMEMBER'
  },
  redirects: {
    vipUnlock: '/pages/unlocked-offers',
    unlockVipPage: '/pages/unlocked-offers',
    cart: '/cart'
  }
};

// State management to prevent race conditions
let isProcessing = false;
let lastCartUpdate = 0;
const CART_UPDATE_DEBOUNCE = 500;

// Helper function to safely convert to string and handle null/undefined
function safeString(value) {
  return value ? String(value) : '';
}

// Helper function to check if cart contains a VIP variant
function cartContainsVipVariant(cart) {
  try {
    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
      return false;
    }

    return cart.items.some(item => {
      try {
        return item && CONFIG.vipVariants.includes(safeString(item.variant_id));
      } catch (e) {
        console.warn('Error checking VIP variant for item:', item, e);
        return false;
      }
    });
  } catch (error) {
    console.error('Error in cartContainsVipVariant:', error);
    return false;
  }
}

function cartReturnsVipId(cart) {
  try {
    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
      return false;
    }

    const vipVariants = cart.items.filter(item => {
      return item && CONFIG.vipVariants.includes(safeString(item.variant_id));
    });
    return vipVariants;
  } catch (error) {
    console.error('Error in cartContainsVipVariant:', error);
    return false;
  }
}

// Helper function to get VIP variant count
function getVipVariantCount(cart) {
  try {
    if (!cart || !Array.isArray(cart.items)) return 0;

    return cart.items.reduce((count, item) => {
      try {
        if (item && CONFIG.vipVariants.includes(safeString(item.variant_id))) {
          return count + (parseInt(item.quantity) || 0);
        }
        return count;
      } catch (e) {
        console.warn('Error counting VIP variants for item:', item, e);
        return count;
      }
    }, 0);
  } catch (error) {
    console.error('Error in getVipVariantCount:', error);
    return 0;
  }
}

// ✅ UPDATED: check if cart contains ANY free product variant, and return detailed info
function cartContainsFreeProduct(cart) {
  try {
    if (!cart || !Array.isArray(cart.items)) {
      return { exists: false, quantity: 0, items: [], itemCount: 0, variants: {} };
    }

    const freeVariantSet = new Set((CONFIG.freeProductVariants || []).map(safeString));

    const freeProductItems = cart.items.filter(item => {
      try {
        return item && freeVariantSet.has(safeString(item.variant_id));
      } catch (e) {
        console.warn('Error checking free product for item:', item, e);
        return false;
      }
    });

    const totalQuantity = freeProductItems.reduce((sum, item) => {
      try {
        return sum + (parseInt(item.quantity) || 0);
      } catch (e) {
        console.warn('Error calculating quantity for item:', item, e);
        return sum;
      }
    }, 0);

    const variants = freeProductItems.reduce((acc, item) => {
      const vid = safeString(item.variant_id);
      acc[vid] = (acc[vid] || 0) + (parseInt(item.quantity) || 0);
      return acc;
    }, {});

    return {
      exists: freeProductItems.length > 0, // true if ANY free variant exists
      quantity: totalQuantity,             // total quantity across all free variants
      items: freeProductItems,
      itemCount: freeProductItems.length,
      variants
    };
  } catch (error) {
    console.error('Error in cartContainsFreeProduct:', error);
    return { exists: false, quantity: 0, items: [], itemCount: 0, variants: {} };
  }
}

// Function to check if a VIP variant has a selling plan
function vipVariantHasSellingPlan(cart) {
  try {
    if (!cart || !Array.isArray(cart.items)) return false;

    const vipItems = cart.items.filter(item =>
      item && CONFIG.vipVariants.includes(safeString(item.variant_id))
    );

    return vipItems.some(item => {
      return item && item.selling_plan_allocation && item.selling_plan_allocation.selling_plan_id;
    });
  } catch (error) {
    console.error('Error in vipVariantHasSellingPlan:', error);
    return false;
  }
}

// Function to get VIP items without selling plans
function getVipItemsWithoutSellingPlans(cart) {
  try {
    if (!cart || !Array.isArray(cart.items)) return [];

    return cart.items.filter(item => {
      return item &&
        CONFIG.vipVariants.includes(safeString(item.variant_id)) &&
        (!item.selling_plan_allocation || !item.selling_plan_allocation.selling_plan_id);
    });
  } catch (error) {
    console.error('Error in getVipItemsWithoutSellingPlans:', error);
    return [];
  }
}

// Function to fetch cart with retry logic and error handling
function withCart(callback, retries = 3) {
  return fetch('/cart.js')
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(cart => {
      // Validate cart structure
      if (!cart || typeof cart !== 'object') {
        throw new Error('Invalid cart data received');
      }

      // Ensure items is an array
      if (!Array.isArray(cart.items)) {
        cart.items = [];
      }

      return callback(cart);
    })
    .catch(error => {
      console.error('Error fetching cart:', error);

      if (retries > 0) {
        console.log(`Retrying cart fetch, ${retries} attempts remaining`);
        return new Promise(resolve => {
          setTimeout(() => {
            resolve(withCart(callback, retries - 1));
          }, 1000);
        });
      }

      // Fallback: call callback with empty cart
      console.warn('Using fallback empty cart');
      return callback({ items: [], total_price: 0, item_count: 0 });
    });
}

// Function to update cart with validation and error handling
function updateCart(updates) {
  if (!updates || typeof updates !== 'object') {
    console.error('Invalid updates object:', updates);
    return Promise.reject(new Error('Invalid updates'));
  }

  // Validate updates - ensure all values are numbers
  const validatedUpdates = {};
  for (const [key, value] of Object.entries(updates)) {
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 0) {
      validatedUpdates[key] = numValue;
    } else {
      console.warn(`Invalid quantity for variant ${key}: ${value}, skipping`);
    }
  }

  if (Object.keys(validatedUpdates).length === 0) {
    console.warn('No valid updates to apply');
    return Promise.resolve({});
  }

  console.log('Updating cart with:', validatedUpdates);

  return fetch('/cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ updates: validatedUpdates })
  })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(result => {
      lastCartUpdate = Date.now();
      return result;
    })
    .catch(error => {
      console.error('Error updating cart:', error);
      throw error;
    });
}

// Function to change cart item with selling plan
function changeCartItemWithSellingPlan(id, quantity = 1, sellingPlan = null) {
  // Validate inputs
  if (!id) {
    console.error('No product ID provided');
    return Promise.reject(new Error('Product ID is required'));
  }

  const validQuantity = parseInt(quantity);
  if (isNaN(validQuantity) || validQuantity < 1) {
    console.error('Invalid quantity:', quantity);
    return Promise.reject(new Error('Valid quantity is required'));
  }

  const data = {
    id: safeString(id),
    quantity: validQuantity
  };

  if (sellingPlan) {
    const validSellingPlan = parseInt(sellingPlan);
    if (!isNaN(validSellingPlan)) {
      data.selling_plan = validSellingPlan;
    }
  }

  console.log('Changing cart item:', data);

  return fetch('/cart/change.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(result => {
      lastCartUpdate = Date.now();
      return result;
    })
    .catch(error => {
      console.error('Error changing item in cart:', error);
      throw error;
    });
}

// Function to add item to cart with validation
function addToCart(id, quantity = 1, selling_plan = null) {
  // Validate inputs
  if (!id) {
    console.error('No product ID provided');
    return Promise.reject(new Error('Product ID is required'));
  }

  const validQuantity = parseInt(quantity);
  if (isNaN(validQuantity) || validQuantity < 1) {
    console.error('Invalid quantity:', quantity);
    return Promise.reject(new Error('Valid quantity is required'));
  }

  const data = {
    id: safeString(id),
    quantity: validQuantity
  };

  if (selling_plan) {
    const validSellingPlan = parseInt(selling_plan);
    if (!isNaN(validSellingPlan)) {
      data.selling_plan = validSellingPlan;
    }
  }

  console.log('Adding to cart:', data);
  localStorage.setItem("Selling Plan", JSON.stringify(data));

  return fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(result => {
      lastCartUpdate = Date.now();
      return result;
    })
    .catch(error => {
      console.error('Error adding item to cart:', error);
      throw error;
    });
}

// Function to show loading overlay
function showLoadingOverlay(message = 'Loading...') {
  // Remove existing overlay if any
  jQuery('#loading-overlay').remove();

  const overlay = `
    <div id="loading-overlay" style="
      position: fixed; 
      top: 0; 
      left: 0; 
      width: 100%; 
      height: 100%; 
      background: rgba(0,0,0,0.7); 
      z-index: 9999; 
      display: flex; 
      justify-content: center; 
      align-items: center; 
      color: white; 
      font-size: 18px;
      font-family: Arial, sans-serif;
    ">
      <div style="text-align: center;">
        <div style="margin-bottom: 20px;">
          <div style="border: 3px solid #f3f3f3; border-top: 3px solid #ffffff; border-radius: 50%; width: 40px; height: 40px; animation: spin 1s linear infinite; margin: 0 auto;"></div>
        </div>
        <div>${message}</div>
      </div>
    </div>
    <style>
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    </style>
  `;

  jQuery('body').append(overlay);
}

// Function to navigate to cart page with loading overlay
function navigateToCart(message = 'Processing your VIP order...') {
  console.log('Navigating to cart page');

  // Show loading overlay
  showLoadingOverlay(message);

  // Navigate to cart immediately
  window.location.href = CONFIG.redirects.cart;
}

// ✅ Enhanced function to synchronize the free product with VIP status and selling plans
function syncFreeProductWithVipStatus() {
  if (isProcessing) {
    console.log('Sync already in progress, skipping');
    return Promise.resolve();
  }

  // Debounce rapid calls
  const now = Date.now();
  if (now - lastCartUpdate < CART_UPDATE_DEBOUNCE) {
    console.log('Debouncing sync call');
    return new Promise(resolve => {
      setTimeout(() => {
        resolve(syncFreeProductWithVipStatus());
      }, CART_UPDATE_DEBOUNCE);
    });
  }

  isProcessing = true;

  return withCart(cart => {
    try {
      const hasVipVariant = cartContainsVipVariant(cart);
      const vipVariantCount = getVipVariantCount(cart);
      const freeProductInfo = cartContainsFreeProduct(cart);
      const hasSellingPlan = vipVariantHasSellingPlan(cart);
      const vipItemsWithoutSellingPlans = getVipItemsWithoutSellingPlans(cart);

      console.log('Cart Sync Analysis:', {
        hasVipVariant,
        vipVariantCount,
        freeProductExists: freeProductInfo.exists,
        freeProductQuantity: freeProductInfo.quantity,
        freeProductItemCount: freeProductInfo.itemCount,
        hasSellingPlan,
        vipItemsWithoutSellingPlansCount: vipItemsWithoutSellingPlans.length
      });

      const preferredFreeVariant = safeString((CONFIG.freeProductVariants || [])[0]);

      // Case 0: Has VIP products but no selling plan - ADD selling plan
      if (hasVipVariant && vipItemsWithoutSellingPlans.length > 0) {
        console.log('Adding selling plan to VIP variants');

        const promises = vipItemsWithoutSellingPlans.map(item => {
          const variantId = safeString(item.variant_id);
          const sellingPlan = CONFIG.sellingPlans[variantId];

          if (sellingPlan) {
            console.log(`Adding selling plan ${sellingPlan} to variant ${variantId}`);
            return changeCartItemWithSellingPlan(
              item.key || item.id, // Use line item key or id
              item.quantity,
              sellingPlan
            ).catch(error => {
              console.error(`Failed to add selling plan to variant ${variantId}:`, error);
              return null;
            });
          }
          return Promise.resolve();
        });

        return Promise.all(promises)
          .then(() => {
            console.log('All selling plans processed, now checking free product');

            return withCart(updatedCart => {
              const updatedFreeProductInfo = cartContainsFreeProduct(updatedCart);
              const updatedHasVipVariant = cartContainsVipVariant(updatedCart);

              // Now handle free product logic
              if (updatedHasVipVariant && !updatedFreeProductInfo.exists) {
                console.log('Adding free product to cart after selling plan update');
                return addToCart(preferredFreeVariant, 1);
              }
              return Promise.resolve(updatedCart);
            });
          });
      }

      // Case 1: Has VIP products but no free product - ADD free product
      if (hasVipVariant && !freeProductInfo.exists) {
        console.log('Adding free product to cart (missing)');
        return addToCart(preferredFreeVariant, 1)
          .catch(error => {
            console.error('Failed to add free product:', error);
            return null;
          });
      }

      // Case 2: Has VIP products but wrong quantity of free product - CORRECT quantity (across both variants)
      if (hasVipVariant && freeProductInfo.exists && freeProductInfo.quantity !== 1) {
        console.log(`Correcting total free product quantity from ${freeProductInfo.quantity} to 1 across variants`);

        const updates = {};
        (CONFIG.freeProductVariants || []).forEach(vid => {
          const id = safeString(vid);
          updates[id] = (id === preferredFreeVariant) ? 1 : 0;
        });

        return updateCart(updates)
          .catch(error => {
            console.error('Failed to correct free product quantity:', error);
            return null;
          });
      }

      // Case 3: No VIP products but has free product - REMOVE free product (remove both variants)
      if (!hasVipVariant && freeProductInfo.exists) {
        console.log('Removing free product from cart (no VIP products)');
        const updates = {};

        (CONFIG.freeProductVariants || []).forEach(vid => {
          updates[safeString(vid)] = 0;
        });

        return updateCart(updates)
          .catch(error => {
            console.error('Failed to remove free product:', error);
            return null;
          });
      }

      // Case 4: Multiple free product line items - CONSOLIDATE to a single preferred variant
      if (hasVipVariant && freeProductInfo.itemCount > 1) {
        console.log('Consolidating multiple free product line items');
        const updates = {};

        (CONFIG.freeProductVariants || []).forEach(vid => {
          const id = safeString(vid);
          updates[id] = (id === preferredFreeVariant) ? 1 : 0;
        });

        return updateCart(updates)
          .catch(error => {
            console.error('Failed to consolidate free product items:', error);
            return null;
          });
      }

      // Case 5: Everything is correct - no action needed
      console.log('Cart sync: No changes needed');
      return Promise.resolve(cart);

    } catch (error) {
      console.error('Error in sync logic:', error);
      return Promise.resolve(cart);
    }
  })
    .finally(() => {
      isProcessing = false;
    });
}

// Function to checkout with appropriate discount based on VIP status
function checkoutWithVipDiscount() {
  return withCart(cart => {
    try {
      const hasVipVariant = cartContainsVipVariant(cart);
      const discountCode = hasVipVariant ?
        CONFIG.discountCodes.vipMember :
        CONFIG.discountCodes.nonVipMember;

      const checkoutUrl = `/cart/checkout/c/?&discount=${encodeURIComponent(discountCode)}`;
      console.log('Redirecting to checkout with discount:', discountCode);
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Error in checkout process:', error);
      // Fallback to regular checkout
      window.location.href = '/cart/checkout';
    }
  });
}

// Enhanced function to handle VIP variant addition to cart
function addVipVariantToCart(variantId) {
  if (!variantId) {
    console.error('No variant ID provided for VIP product');
    return Promise.reject(new Error('Variant ID is required'));
  }

  // Validate variant ID is actually a VIP variant
  if (!CONFIG.vipVariants.includes(safeString(variantId))) {
    console.error('Invalid VIP variant ID:', variantId);
    return Promise.reject(new Error('Invalid VIP variant'));
  }

  jQuery("body").addClass("disabled-active");

  const sellingPlan = CONFIG.sellingPlans[variantId];
  let operationSuccessful = false;

  return addToCart(variantId, 1, sellingPlan)
    .then(result => {
      console.log('VIP product added successfully:', result);
      operationSuccessful = true;

      // Wait a moment for cart to update, then sync free product
      return new Promise(resolve => {
        setTimeout(() => {
          resolve(syncFreeProductWithVipStatus());
        }, 300);
      });
    })
    .then(() => {
      if (operationSuccessful) {
        // Check if free product exists after sync
        return withCart(cart => {
          const freeProductInfo = cartContainsFreeProduct(cart);

          if (freeProductInfo.exists) {
            console.log('Free product found, navigating to cart');
            navigateToCart('Processing your VIP order...');
          } else {
            console.log('No free product found, redirecting to VIP unlock page');
            window.location.href = CONFIG.redirects.vipUnlock;
          }
        });
      }
    })
    .catch(error => {
      console.error('Error adding VIP variant to cart:', error);

      // Show user-friendly error message
      if (typeof $ !== 'undefined' && $.alert) {
        $.alert({
          title: 'Error',
          content: 'There was an issue adding the product to your cart. Please try again.',
          buttons: {
            OK: function() { }
          }
        });
      } else {
        alert('There was an issue adding the product to your cart. Please try again.');
      }
    })
    .finally(() => {
      jQuery("body").removeClass("disabled-active");
    });
}

// Function to check cart item and apply discount for checkout drawer
function checkCartItemAndApplyDiscount(variantId, discountCode) {
  if (!variantId || !discountCode) {
    console.error('Missing parameters for discount check');
    return Promise.resolve();
  }

  return withCart(cart => {
    try {
      const itemFound = cart.items.some(item => {
        try {
          return item && safeString(item.variant_id) === safeString(variantId);
        } catch (e) {
          console.warn('Error checking item in discount function:', item, e);
          return false;
        }
      });

      const encodedDiscount = encodeURIComponent(discountCode);

      if (itemFound) {
        window.location.href = `/cart/checkout?&discount=${encodedDiscount}`;
      } else {
        window.location.href = "/cart/checkout?&discount=''";
      }
    } catch (error) {
      console.error('Error in discount check:', error);
      // Fallback to regular checkout
      window.location.href = '/cart/checkout';
    }
  });
}

// Function to remove VIP products from cart with confirmation
function removeVipProductsFromCart() {
  try {
    const updates = {};

    // Remove all VIP variants
    CONFIG.vipVariants.forEach(variantId => {
      if (variantId) {
        updates[variantId] = 0;
      }
    });

    // ✅ Remove all FREE variants
    (CONFIG.freeProductVariants || []).forEach(vid => {
      updates[safeString(vid)] = 0;
    });

    console.log('Removing VIP products:', updates);

    return updateCart(updates)
      .then(() => {
        console.log('VIP products removed, redirecting to cart');
        window.location.href = CONFIG.redirects.cart;
      })
      .catch(error => {
        console.error('Error removing VIP products:', error);
        // Still redirect to cart even if removal failed
        window.location.href = CONFIG.redirects.cart;
      });
  } catch (error) {
    console.error('Error in removeVipProductsFromCart:', error);
    window.location.href = CONFIG.redirects.cart;
  }
}

// Function to handle continue to cart redirect based on VIP status
function handleContinueToCartRedirect() {
  return withCart(cart => {
    try {
      const hasVipVariant = cartContainsVipVariant(cart);
      const freeProductInfo = cartContainsFreeProduct(cart);

      if (hasVipVariant && freeProductInfo.exists) {
        console.log('Has VIP products and free product - navigating to cart');
        navigateToCart('Loading your cart...');
      } else if (hasVipVariant) {
        console.log('Has VIP products but no free product - redirecting to cart');
        window.location.href = CONFIG.redirects.cart;
      } else {
        console.log('No VIP products - redirecting to unlock VIP page');
        window.location.href = CONFIG.redirects.unlockVipPage;
      }
    } catch (error) {
      console.error('Error in continue to cart redirect:', error);
      // Default to cart page
      window.location.href = CONFIG.redirects.cart;
    }
  });
}

// Enhanced debounce function with immediate execution option
function debounce(func, wait, immediate = false) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func(...args);
  };
}

// Throttle function to limit function execution frequency
function throttle(func, limit) {
  let inThrottle;
  return function (...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

// Debounced and throttled sync functions
const debouncedSync = debounce(syncFreeProductWithVipStatus, 300);
const throttledSync = throttle(syncFreeProductWithVipStatus, 1000);

// Safe element selector with error handling
function safeGetElement(selector) {
  try {
    return document.querySelector(selector);
  } catch (error) {
    console.warn('Error selecting element:', selector, error);
    return null;
  }
}

// Safe value extraction with fallback
function safeGetValue(element, attribute = 'value', fallback = '') {
  try {
    if (!element) return fallback;

    if (attribute === 'value') {
      return element.value || fallback;
    } else {
      return element.getAttribute(attribute) || fallback;
    }
  } catch (error) {
    console.warn('Error getting element value:', error);
    return fallback;
  }
}

// Initialize when document is ready
$(document).ready(function() {
  console.log('Enhanced VIP Member Scripts Initialized');
  
  // Run initial sync with delay to ensure DOM is fully loaded
  setTimeout(() => {
    syncFreeProductWithVipStatus();
  }, 500);

  // Add event listener for add to cart buttons
  // $(document).on("click", '.add_to_cart:not([name="checkout"])', function(event) {
  //   try {
  //     event.preventDefault();
  //     event.stopPropagation();
  //     event.stopImmediatePropagation();
      
  //     jQuery("body").addClass("disabled-active");
      
  //     const variantElement = safeGetElement('input[name="variant_id"]');
  //     const variantId = safeGetValue(variantElement);
      
  //     if (!variantId) {
  //       console.error('No variant ID found');
  //       jQuery("body").removeClass("disabled-active");
  //       return;
  //     }
      
  //     console.log('Add to cart clicked, variant:', variantId);
      
  //     // Check if this is a VIP variant
  //     if (CONFIG.vipVariants.includes(safeString(variantId))) {
  //       addVipVariantToCart(variantId);
  //     } else {
  //       // Regular product - add it, then sync free product status
  //       const sellingPlan = CONFIG.sellingPlans[variantId];
  //       addToCart(variantId, 1, sellingPlan)
  //         .then(() => {
  //           console.log('Regular product added, syncing free product');
  //           return debouncedSync();
  //         })
  //         .then(() => {
  //           // Check if free product exists in cart after sync
  //           return withCart(cart => {
  //             const freeProductInfo = cartContainsFreeProduct(cart);
              
  //             if (freeProductInfo.exists) {
  //               console.log('Free product found, navigating to cart');
  //               navigateToCart('Processing your order...');
  //             } else {
  //               console.log('No free product found, redirecting to unlock page');
  //               window.location.href = CONFIG.redirects.unlockVipPage;
  //             }
  //           });
  //         })
  //         .catch(error => {
  //           console.error('Error adding regular product:', error);
  //           // Fallback to unlock page on error
  //           window.location.href = CONFIG.redirects.unlockVipPage;
  //         })
  //         .finally(() => {
  //           jQuery("body").removeClass("disabled-active");
  //         });
  //     }
  //   } catch (error) {
  //     console.error('Error in add to cart click:', error);
  //     jQuery("body").removeClass("disabled-active");
  //   }
  // });

      $('.HeroAmbassadorClub').click(function(e) {
        var dialogResult = "";
          $.confirm({
              title: 'Are you sure you want to remove "STRĀ Ambassador Club" from cart?',
              content: 'Removing "STRĀ Ambassador Club" product will refresh your cart and remove discount coupon, free product from your cart!',
              buttons: {
                  Yes: function () {
                      dialogResult = "Yes";

                    //window.location.href = '/cart/clear';

                              // Get the line item IDs and quantities for the products to be removed
                              var data = {
                                "updates": {
                                  "46436299505856": 0,
                                  "46579815874752": 0,
                                  "47256335974592": 0
                                }
                              };
                                e.preventDefault();
                              // Make an AJAX request to remove the products from the cart
                              $.ajax({
                                type: 'POST',
                                url: '/cart/update.js',
                                data: data,
                                dataType: 'json',
                                success: function() {
                                  // If the request is successful, redirect the user to the cart page
                                  window.location.href = '/cart';
                                }
                              });



                  },
                  No: function () {
                      dialogResult = "No";
                      e.preventDefault();
                  },
              }
          });
      });

});



